import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import styles from "./CustomizeStandardPage.module.css";
import PageHeader from "NewsAdmin/Shared/PageHeader";
import FeedBanner from "NewsAdmin/Shared/FeedBanner";
import DeleteFeedItemBar from "NewsAdmin/Shared/DeleteFeedItemBar";
import StandardContentDropdownList from "NewsAdmin/Shared/StandardContentDropdownList";
import FeedItemPreview from "NewsAdmin/Shared/FeedItemPreview";
import { useEditFeedPage } from "../EditFeedPage/useEditFeedPage";
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";
import Loader from "shared/Loader";
import dayjs from "dayjs";
import { useFeedItemOptionsDropdown } from "NewsAdmin/Shared/FeedItemOptionsDropdown/useFeedItemOptionsDropdown";
import NotificationOverlay from "NewsAdmin/Shared/NotificationOverlay";
import ConfirmationDialog from "NewsAdmin/Shared/ConfirmationDialog";
import moment from "moment-timezone";

export function CustomizeStandardPage({
  isNew,
  isCloning = false,
  justAdded = false,
  feedItem: incomingFeedItem,
  feedItemId,
  setPage,
  diamondsList,
  groupsList
}) {
  const history = useHistory();
  const queryClient = useQueryClient();
  const defaultImageUrl =
    "https://wwgus.blob.core.windows.net/icons/bsm-image-coming-soon.png";

  const defaultFeedItem = {
    type: "Standard Content",
    imgSrc: defaultImageUrl,
    options: {
      title: "* Standard Content Title *",
      type: "standard",
      previewIBO: 0,
      pinLevelPermissions: ["AssociateIbo", "Platinum", "Emerald", "Diamond"],
      familyReunions: [],
      diamonds: [],
      groups: [],
      platforms: ["Web", "iOS", "Android"],
      shouldSendUpdateIcon: true,
      status: 0,
      availableOn: moment()
        .hour(17)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate(),
      unpublishOn: "",
      unpublishType: 0,
      isEditingDraft: false,
      managementApproved: false,
      group: 1,
      priority: 5,
    },
    information: {
      header: "* Card Title *",
      subHeader: "",
      headerTextColor: "#fff",
      subHeaderTextColor: "default",
      backgroundType: "On Gradient",
      horizontalAlign: "center",
      imgSrc: defaultImageUrl,
      bottomPadding: true,
      maxHeight: "",
      gradientAngle: "45",
      headerBackground: {
        backgroundColor: "default",
        backgroundOpacity: "0.80",
      },
      gradientColors: ["#006837", "#fff"],
    },
    content: {
      sections: [],
    },
  };

  // Build the merged feed item
  const initialMergedFeedItem = {
    ...(isNew
      ? defaultFeedItem
      : {
          ...defaultFeedItem,
          ...incomingFeedItem,
          options: {
            ...defaultFeedItem.options,
            ...incomingFeedItem?.options,
          },
          information: {
            ...defaultFeedItem.information,
            ...incomingFeedItem?.information,
          },
          content: {
            ...defaultFeedItem.content,
            ...(incomingFeedItem?.content ||
              incomingFeedItem?.information?.content),
          },
          template: incomingFeedItem?.template || "Standard",
        }),
  };

  const [feedItem, setFeedItem] = useState(initialMergedFeedItem);
  // useEffect(() => console.log(feedItem), [feedItem]);
  const initialFeedItemRef = useRef(cloneDeep(initialMergedFeedItem));

  const [hasChanged, setHasChanged] = useState(isNew);
  const [hasPersistedChangesOnServer, setHasPersistedChangesOnServer] =
    useState(Boolean(justAdded));
  const [isDraft, setIsDraft] = useState(
    incomingFeedItem?.options?.isEditingDraft || false
  );
  const [resetKey, setResetKey] = useState(0);

  const [showAlert, setShowAlert] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const triggerAlert = () => {
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 1000);
  };

  // Change tracking based on deep compare
  useEffect(() => {
    const changed = !isEqual(feedItem, initialFeedItemRef.current);
    setHasChanged(changed);
  }, [feedItem]);

  const onBackArrow = () => {
    if (!hasPersistedChangesOnServer) {
      history.push(`/marketing/news/feed`);
      return;
    }
    history.push(`/marketing/news/feed`, {
      showFeedLoader: true,
      expectedFeedId: feedItem?.options?.id || feedItemId || null,
      expectedFeedTitle: hasChanged ? null : feedItem?.options?.title || null,
    });
  };

  const onReset = () => {
    if (!isNew) {
      setFeedItem(cloneDeep(initialFeedItemRef.current));
      setResetKey((prevKey) => prevKey + 1);
    } else {
      setPage("create");
    }
  };

  const {
    saveFeedItem,
    saveAsDraft,
    createFeedItem,
    deleteFeedItem,
  } = useEditFeedPage();

  const onSave = () => {
    if (isNew || isCloning) {
      createFeedItem(
        { feedItem, diamondsList, groupsList },
        {
          onSuccess: (itemId) => {
            history.push(`/marketing/news/feed/${itemId}`, {
              pageType: "Standard Content",
              justAdded: true,
            });
          },
          onError: (err) => {
            console.error("Create failed:", err);
          },
        }
      );
    } else {
      saveFeedItem(
        { feedItem, diamondsList, groupsList },
        {
          onSuccess: (updatedItem) => {
            const merged = {
              ...feedItem,
              ...updatedItem,
              information: {
                ...feedItem.information,
                card: {
                  gradient: updatedItem?.data?.card?.gradient,
                  header: updatedItem?.data?.card?.header,
                  image: updatedItem?.data?.card?.image,
                },
              },
              content: {
                ...feedItem.content,
                ...updatedItem.content,
              },
            };

            setFeedItem(merged);
            initialFeedItemRef.current = cloneDeep(merged);

            queryClient.setQueryData(["feedItem", updatedItem.Id], updatedItem);
            queryClient.invalidateQueries(["feed"]);

            setIsDraft(false);
            setHasChanged(false);
            setHasPersistedChangesOnServer(true);
            setResetKey((prevKey) => prevKey + 1);
            triggerAlert();
            // history.push("/marketing/news/feed");
          },
          onError: (err) => {
            console.error("Save failed:", err);
          },
        }
      );
    }
  };

  const onDraft = () => {
    if (isNew || isCloning) {
      createFeedItem(
        { feedItem, diamondsList, groupsList, isDraft: true },
        {
          onSuccess: () => {
            history.push("/marketing/news/feed");
          },
          onError: (err) => {
            console.error("Create failed:", err);
          },
        }
      );
    } else {
      saveAsDraft(
        { feedItem, diamondsList, groupsList },
        {
          onSuccess: async (updatedItem) => {
            const merged = {
              ...feedItem,
              ...updatedItem,
              information: {
                ...feedItem.information,
                card: {
                  gradient: updatedItem?.data?.card?.gradient,
                  header: updatedItem?.data?.card?.header,
                  image: updatedItem?.data?.card?.image,
                },
              },
              content: {
                ...feedItem.content,
                ...updatedItem.content,
              },
            };

            setFeedItem(merged);
            initialFeedItemRef.current = cloneDeep(merged);

            queryClient.setQueryData(["feedItem", updatedItem.Id], updatedItem);
            queryClient.invalidateQueries(["feed"]);

            setIsDraft(true);
            setHasChanged(false);
            setHasPersistedChangesOnServer(true);
            setResetKey((prevKey) => prevKey + 1);
            triggerAlert();
            // window.location.href = "/marketing/news/feed";
          },
          onError: (err) => {
            console.error("Draft failed:", err);
          },
        }
      );
    }
  };

  const onDelete = () => {
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = () => {
    setShowDeleteConfirm(false);
    deleteFeedItem(
      { id: feedItem.options.id || feedItemId },
      {
        onSuccess: () => {
          history.push("/marketing/news/feed");
        },
        onError: (err) => {
          console.error("Delete failed:", err);
        },
      }
    );
  };

  if (!feedItem) {
    return <Loader />;
  }

  return (
    <div className={styles.pageContainer}>
      <ConfirmationDialog
        isVisible={showDeleteConfirm}
        confirmationText="Are you sure you want to delete this feed item? This action cannot be undone."
        onConfirmation={onConfirmDelete}
        onDismiss={() => setShowDeleteConfirm(false)}
      />
      <PageHeader onClick={onBackArrow} isHome={false} />
      <div className={styles.scrollableList}>
        <FeedBanner
          feedType={feedItem.type}
          feedTitle={feedItem.options?.title || ""}
          status={isNew ? "New" : isCloning ? "Clone" : "Edit"}
          isChangedOrNew={isNew ? true: hasChanged}
          isDraft={isDraft}
          showDraft={!isNew && !isCloning}
          onCancel={onBackArrow}
          onReset={onReset}
          onSave={onSave}
          onDraft={onDraft}
        />

        <div className={styles.contentContainer}>
          <div className={styles.flex}>
            <div className={styles.dropdown} key={resetKey}>
              {feedItem && (
                <StandardContentDropdownList
                  feedItem={feedItem}
                  setFeedItem={setFeedItem}
                  isNew={isNew}
                />
              )}
            </div>

            <div className={styles.preview}>
              <FeedItemPreview feedItem={feedItem} />
            </div>
          </div>
        </div>

        {!isDraft && !isNew ? (
          <DeleteFeedItemBar onClick={onDelete} />
        ) : (
          <div className={styles.emptyFooterBar} />
        )}
      </div>

      <NotificationOverlay
        isVisible={showAlert}
        overlayText="Feed Item Updated"
      />
    </div>
  );
}
